import { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

// Role/plan eligibility mirrors the backend route guards so the hook never
// fires requests that are guaranteed to 403.
const SUFFICIENCY_ROLES = ['admin', 'head', 'compliance', 'view_only', 'portfolio_adviser'];
const SIGNAL_ROLES = ['admin', 'head', 'compliance', 'portfolio_adviser'];
const ACTION_ROLES = ['admin', 'rm'];

export const FIRM_DATASET_TYPES = [
    ['products', 'Product catalog'],
    ['holdings', 'Customer holdings'],
    ['transactions', 'Customer transactions'],
    ['suitability_evidence', 'Suitability evidence'],
    ['model_portfolio_targets', 'Model portfolio targets'],
];

const signalTypeFromId = (signalId = '') => {
    if (signalId.startsWith('drift-')) return 'drift';
    if (signalId.startsWith('missing-model-target-')) return 'model_gap';
    if (signalId.startsWith('missing-catalog-')) return 'missing_catalog';
    if (signalId.startsWith('transaction-evidence-gap-')) return 'transaction_gap';
    if (signalId.startsWith('evidence-')) return 'evidence';
    if (signalId.startsWith('risk-mismatch-')) return 'risk_mismatch';
    if (signalId.startsWith('knowledge-mismatch-')) return 'knowledge_mismatch';
    return 'other';
};

export const categorizeSignals = (signalList = []) => {
    const byType = {
        drift: [],
        model_gap: [],
        missing_catalog: [],
        evidence: [],
        transaction_gap: [],
        risk_mismatch: [],
        knowledge_mismatch: [],
        other: [],
    };
    signalList.forEach((signal) => {
        byType[signalTypeFromId(signal.signal_id)].push(signal);
    });
    return byType;
};

export const severityTone = (severity) => {
    if (severity === 'critical' || severity === 'high') return 'high';
    if (severity === 'medium') return 'medium';
    return 'ok';
};

export const formatPct = (value) => (
    Number.isFinite(Number(value)) ? `${Math.round(Number(value))}%` : '—'
);

// Fetches the live tenant operating context (data sufficiency, product-data
// signals, RM follow-up actions) used by the role "Today" workspaces.
// Every fetch is best-effort: a failed call resolves to null so the views can
// degrade to honest empty states instead of synthetic numbers.
export default function useTodayWorkspaceData({ withSignals = true, withActions = false } = {}) {
    const { token, user } = useContext(AuthContext);
    const [state, setState] = useState({ loading: true, sufficiency: null, signals: null, actions: null });

    const subscription = user?.subscription;
    const role = user?.role;

    useEffect(() => {
        if (!token || !subscription) {
            setState({ loading: false, sufficiency: null, signals: null, actions: null });
            return undefined;
        }

        let cancelled = false;
        const headers = { Authorization: `Bearer ${token}` };

        const canSufficiency = ['solo', 'pro', 'business'].includes(subscription) && SUFFICIENCY_ROLES.includes(role);
        const canSignals = withSignals && subscription === 'business' && SIGNAL_ROLES.includes(role);
        const canActions = withActions && ['solo', 'pro', 'business'].includes(subscription) && ACTION_ROLES.includes(role);

        const safeGet = async (url, params) => {
            try {
                const response = await axios.get(url, { headers, params });
                return response.data;
            } catch (error) {
                return null;
            }
        };

        const load = async () => {
            const [sufficiency, signals, actions] = await Promise.all([
                canSufficiency ? safeGet(`${API_BASE_URL}/content/product-data/sufficiency`) : Promise.resolve(null),
                canSignals ? safeGet(`${API_BASE_URL}/content/product-data/signals`, { limit: 200 }) : Promise.resolve(null),
                canActions ? safeGet(`${API_BASE_URL}/content/rm-actions`) : Promise.resolve(null),
            ]);

            if (!cancelled) {
                setState({
                    loading: false,
                    sufficiency,
                    signals,
                    actions: Array.isArray(actions) ? actions : null,
                });
            }
        };

        setState((prev) => ({ ...prev, loading: true }));
        load();

        return () => {
            cancelled = true;
        };
    }, [token, subscription, role, withSignals, withActions]);

    return useMemo(() => {
        const counts = state.sufficiency?.counts || {};
        const coverage = state.sufficiency?.coverage || {};
        const signalList = state.signals?.signals || [];
        const byType = categorizeSignals(signalList);
        // The engine counts every signal in the book; the list is a sample, so
        // counts must come from totals_by_type (list lengths are a fallback for
        // older engines).
        const totals = state.signals?.totals_by_type || {};
        const typeCounts = Object.fromEntries(Object.keys(byType).map((kind) => [
            kind, Number.isFinite(Number(totals[kind])) ? Number(totals[kind]) : byType[kind].length,
        ]));
        // Critical + high count per category, over the whole book when the engine provides it.
        const severityTotals = state.signals?.totals_by_type_severity;
        const highSeverityCount = (kinds) => kinds.reduce((sum, kind) => {
            if (severityTotals) {
                const bucket = severityTotals[kind] || {};
                return sum + (bucket.critical || 0) + (bucket.high || 0);
            }
            return sum + (byType[kind] || []).filter((signal) => ['critical', 'high'].includes(signal.severity)).length;
        }, 0);
        const listWorstDrift = byType.drift.reduce(
            (max, signal) => Math.max(max, Math.abs(Number(signal.evidence?.drift_pct || 0))), 0,
        );
        const worstDriftPp = state.signals && 'worst_drift_pp' in state.signals
            ? state.signals.worst_drift_pp
            : (byType.drift.length ? listWorstDrift : null);

        return {
            loading: state.loading,
            sufficiency: state.sufficiency,
            counts,
            coverage,
            nextBestUploads: state.sufficiency?.next_best_uploads || [],
            totalSignals: state.signals?.total_signals ?? null,
            signals: signalList,
            signalsAvailable: state.signals !== null,
            byType,
            typeCounts,
            worstDriftPp,
            highSeverityCount,
            actions: state.actions,
        };
    }, [state]);
}
